import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle, User, Calendar, MessageSquare, Send, Eye } from 'lucide-react';
import { WorkflowService, WorkflowStatus, TakeStepActionRequest } from '../services/WorkflowService';
import { useAuth } from '../contexts/AuthContext';

interface WorkflowApprovalFlowProps {
  expenseId: number;
  onStatusChange?: (status: string) => void;
}

const WorkflowApprovalFlow: React.FC<WorkflowApprovalFlowProps> = ({
  expenseId,
  onStatusChange
}) => {
  const { user } = useAuth();
  const [workflowStatus, setWorkflowStatus] = useState<WorkflowStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isApprover, setIsApprover] = useState<boolean>(false);
  const [canViewExpense, setCanViewExpense] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [actionNotes, setActionNotes] = useState<{ [key: number]: string }>({});
  const [rejectionReason, setRejectionReason] = useState<{ [key: number]: string }>({});
  const [escalationReason, setEscalationReason] = useState<{ [key: number]: string }>({});

  useEffect(() => {
    loadWorkflowStatus();
    checkUserPermissions();
  }, [expenseId]);

  const checkUserPermissions = async () => {
    try {
      const [approverStatus, viewStatus] = await Promise.all([
        WorkflowService.isUserApproverForExpense(expenseId),
        WorkflowService.canUserViewExpense(expenseId)
      ]);
      setIsApprover(approverStatus);
      setCanViewExpense(viewStatus);
    } catch (err) {
      console.error('Error checking user permissions:', err);
      setIsApprover(false);
      setCanViewExpense(false);
    }
  };

  const loadWorkflowStatus = async () => {
    try {
      setLoading(true);
      const response = await WorkflowService.getExpenseWorkflowStatus(expenseId);
      console.log('WorkflowApprovalFlow - Received workflow status:', response);
      
      // Check if there's no active workflow
      if (response && 'status' in response && response.status === 'no_workflow') {
        console.log('WorkflowApprovalFlow - No active workflow found');
        setWorkflowStatus(null);
        return;
      }
      
      console.log('WorkflowApprovalFlow - Steps:', response.steps);
      setWorkflowStatus(response);
      onStatusChange?.(response.workflow_instance.status);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load workflow status');
    } finally {
      setLoading(false);
    }
  };

  const executeAction = async (stepId: number, action: 'approve' | 'reject' | 'escalate' | 'delegate') => {
    try {
      setActionLoading(stepId);
      
      const actionRequest: TakeStepActionRequest = {
        step_instance_id: stepId,
        action,
        notes: actionNotes[stepId] || '',
        rejection_reason: action === 'reject' ? rejectionReason[stepId] : undefined,
        escalation_reason: action === 'escalate' ? escalationReason[stepId] : undefined,
      };

      await WorkflowService.executeWorkflowAction(expenseId, actionRequest);
      
      // Reload workflow status
      await loadWorkflowStatus();
      
      // Clear form data
      setActionNotes(prev => ({ ...prev, [stepId]: '' }));
      setRejectionReason(prev => ({ ...prev, [stepId]: '' }));
      setEscalationReason(prev => ({ ...prev, [stepId]: '' }));
      
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to execute action');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'APPROVED':
      case 'COMPLETED':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'PENDING':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'IN_PROGRESS':
        return <Clock className="h-5 w-5 text-green-500" />;
      case 'ESCALATED':
        return <AlertCircle className="h-5 w-5 text-orange-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-green-100 text-green-800';
      case 'escalated':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Loading workflow status...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      </div>
    );
  }

  // Show workflow only to users who can view the expense (creators or approvers)
  if (!canViewExpense) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Eye className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Workflow Restricted</h3>
        <p className="text-gray-600">
          Workflow details are only visible to expense creators and assigned approvers.
        </p>
      </div>
    );
  }

  if (!workflowStatus) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Workflow Completed</h3>
        <p className="text-gray-600">
          This expense has completed its approval workflow. No active workflow steps are available.
        </p>
      </div>
    );
  }

  const { workflow_instance, steps, current_step, progress, summary } = workflowStatus;

  return (
    <div className="space-y-6">
      {/* Workflow Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            {getStatusIcon(workflow_instance.status)}
            <h3 className="text-lg font-semibold text-gray-900 ml-2">
              Approval Workflow
            </h3>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(workflow_instance.status)}`}>
            {workflow_instance.status.replace('_', ' ')}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>{progress.completed_steps} of {progress.total_steps} steps</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress.percentage}%` }}
            ></div>
          </div>
        </div>

        {/* Current Step Info */}
        {current_step && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-green-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-green-900">
                  Current Step: {current_step.step_name}
                </p>
                <p className="text-sm text-green-700">
                  Assigned to: {current_step.assigned_to}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Workflow Summary */}
        <div className="mt-4 text-sm text-gray-600">
          <p><strong>Started:</strong> {formatDate(workflow_instance.started_at)}</p>
          {workflow_instance.completed_at && (
            <p><strong>Completed:</strong> {formatDate(workflow_instance.completed_at)}</p>
          )}
          {summary.next_action && (
            <p><strong>Next Action:</strong> {summary.next_action}</p>
          )}
        </div>
      </div>

      {/* Workflow Steps */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold text-gray-900">Workflow Steps</h4>
            {!isApprover && canViewExpense && (
              <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                Read-only view
              </span>
            )}
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {steps.map((step, index) => {
            console.log(`WorkflowApprovalFlow - Rendering step ${step.step_number}: ${step.step_name} with status: ${step.status}`);
            return (
            <div key={step.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    {getStatusIcon(step.status)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h5 className="text-sm font-medium text-gray-900">
                        Step {step.step_number}: {step.step_name}
                      </h5>
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(step.status)}`}>
                        {step.status.replace('_', ' ')}
                      </span>
                    </div>
                    
                    {step.assigned_user_id && (
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <User className="h-4 w-4 mr-1" />
                        Assigned to User ID: {step.assigned_user_id}
                      </div>
                    )}
                    
                    {step.started_at && (
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <Calendar className="h-4 w-4 mr-1" />
                        Started: {formatDate(step.started_at)}
                      </div>
                    )}
                    
                    {step.completed_at && (
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Completed: {formatDate(step.completed_at)}
                      </div>
                    )}
                    
                    {step.step_notes && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-md">
                        <div className="flex items-start">
                          <MessageSquare className="h-4 w-4 mr-2 mt-0.5 text-gray-400" />
                          <p className="text-sm text-gray-700">{step.step_notes}</p>
                        </div>
                      </div>
                    )}
                    
                    {step.rejection_reason && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-800">
                          <strong>Rejection Reason:</strong> {step.rejection_reason}
                        </p>
                      </div>
                    )}
                    
                    {step.escalation_reason && (
                      <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-md">
                        <p className="text-sm text-orange-800">
                          <strong>Escalation Reason:</strong> {step.escalation_reason}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Message for creators when steps are pending */}
              {(step.status === 'pending' || step.status === 'in_progress') && !isApprover && canViewExpense && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-green-500 mr-2" />
                    <p className="text-sm text-green-700">
                      This step is awaiting approval from the assigned approver.
                    </p>
                  </div>
                </div>
              )}

              {/* Action Buttons for Pending and In-Progress Steps - Only for Approvers */}
              {(step.status === 'pending' || step.status === 'in_progress') && isApprover && (
                <div className="mt-4 space-y-4">
                  {/* Notes Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Add Notes (Optional)
                    </label>
                    <textarea
                      value={actionNotes[step.id] || ''}
                      onChange={(e) => setActionNotes(prev => ({ ...prev, [step.id]: e.target.value }))}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Add any notes or comments..."
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <button
                      onClick={() => executeAction(step.id, 'approve')}
                      disabled={actionLoading === step.id}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === step.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      Approve
                    </button>

                    <button
                      onClick={() => executeAction(step.id, 'reject')}
                      disabled={actionLoading === step.id}
                      className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === step.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : (
                        <XCircle className="h-4 w-4 mr-2" />
                      )}
                      Reject
                    </button>

                    <button
                      onClick={() => executeAction(step.id, 'escalate')}
                      disabled={actionLoading === step.id}
                      className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {actionLoading === step.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : (
                        <AlertCircle className="h-4 w-4 mr-2" />
                      )}
                      Escalate
                    </button>
                  </div>

                  {/* Rejection Reason Input */}
                  {rejectionReason[step.id] !== undefined && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rejection Reason *
                      </label>
                      <textarea
                        value={rejectionReason[step.id] || ''}
                        onChange={(e) => setRejectionReason(prev => ({ ...prev, [step.id]: e.target.value }))}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Please provide a reason for rejection..."
                      />
                    </div>
                  )}

                  {/* Escalation Reason Input */}
                  {escalationReason[step.id] !== undefined && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Escalation Reason *
                      </label>
                      <textarea
                        value={escalationReason[step.id] || ''}
                        onChange={(e) => setEscalationReason(prev => ({ ...prev, [step.id]: e.target.value }))}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Please provide a reason for escalation..."
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WorkflowApprovalFlow;
